import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Edit } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface Column {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date';
}

interface CustomDataTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialData?: any;
}

export const CustomDataTableModal: React.FC<CustomDataTableModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData
}) => {
  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState<Column[]>([
    { id: '1', name: 'Colonne 1', type: 'text' }
  ]);
  const [rows, setRows] = useState<Record<string, any>[]>([{}]);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; columnId: string } | null>(null);

  // Load initial data when modal opens
  React.useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setTableName(initialData.name || '');
        setColumns(initialData.columns || [{ id: '1', name: 'Colonne 1', type: 'text' }]);
        setRows(initialData.rows || [{}]);
      } else {
        // Reset to default when creating new table
        setTableName('');
        setColumns([{ id: '1', name: 'Colonne 1', type: 'text' }]);
        setRows([{}]);
      }
      setEditingCell(null);
    }
  }, [isOpen, initialData]);

  const addColumn = () => {
    const newColumn: Column = {
      id: Date.now().toString(),
      name: `Colonne ${columns.length + 1}`,
      type: 'text'
    };
    setColumns([...columns, newColumn]);
    
    // Ajouter la nouvelle colonne à toutes les lignes existantes
    setRows(rows.map(row => ({ ...row, [newColumn.id]: '' })));
  };

  const removeColumn = (columnId: string) => {
    setColumns(columns.filter(col => col.id !== columnId));
    setRows(rows.map(row => {
      const newRow = { ...row };
      delete newRow[columnId];
      return newRow;
    }));
  };

  const updateColumn = (columnId: string, updates: Partial<Column>) => {
    setColumns(columns.map(col => 
      col.id === columnId ? { ...col, ...updates } : col
    ));
  };

  const addRow = () => {
    const newRow: Record<string, any> = {};
    columns.forEach(col => {
      newRow[col.id] = '';
    });
    setRows([...rows, newRow]);
  };

  const removeRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateCell = (rowIndex: number, columnId: string, value: any) => {
    const newRows = [...rows];
    newRows[rowIndex] = { ...newRows[rowIndex], [columnId]: value };
    setRows(newRows);
  };

  const handleSave = () => {
    if (!tableName.trim()) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir un nom pour le tableau",
        variant: "destructive"
      });
      return;
    }

    if (columns.length === 0) {
      toast({
        title: "Erreur", 
        description: "Le tableau doit avoir au moins une colonne",
        variant: "destructive"
      });
      return;
    }

    onSave({
      name: tableName,
      columns,
      rows: rows.filter(row => Object.values(row).some(val => val !== ''))
    });
  };

  const handleCellEdit = (rowIndex: number, columnId: string, value: string) => {
    updateCell(rowIndex, columnId, value);
  };

  const navigateToCell = (direction: 'next' | 'previous' | 'down' | 'up') => {
    if (!editingCell) return;

    const currentRowIndex = editingCell.rowIndex;
    const currentColumnIndex = columns.findIndex(col => col.id === editingCell.columnId);

    let newRowIndex = currentRowIndex;
    let newColumnIndex = currentColumnIndex;

    switch (direction) {
      case 'next':
        newColumnIndex = currentColumnIndex + 1;
        if (newColumnIndex >= columns.length) {
          newColumnIndex = 0;
          newRowIndex = currentRowIndex + 1;
        }
        break;
      case 'previous':
        newColumnIndex = currentColumnIndex - 1;
        if (newColumnIndex < 0) {
          newColumnIndex = columns.length - 1;
          newRowIndex = currentRowIndex - 1;
        }
        break;
      case 'down':
        newRowIndex = currentRowIndex + 1;
        break;
      case 'up':
        newRowIndex = currentRowIndex - 1;
        break;
    }

    // Vérifier les limites
    if (newRowIndex >= 0 && newRowIndex < rows.length && newColumnIndex >= 0 && newColumnIndex < columns.length) {
      setEditingCell({ rowIndex: newRowIndex, columnId: columns[newColumnIndex].id });
    } else {
      setEditingCell(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, columnId: string) => {
    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          navigateToCell('previous');
        } else {
          navigateToCell('next');
        }
        break;
      case 'Enter':
        e.preventDefault();
        navigateToCell('down');
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateToCell('up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        navigateToCell('down');
        break;
      case 'Escape':
        setEditingCell(null);
        break;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex-col hidden sm:flex">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 'Modifier le tableau' : 'Créer un nouveau tableau'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto space-y-6">
          {/* Nom du tableau */}
          <div className="space-y-2">
            <Label htmlFor="tableName">Nom du tableau</Label>
            <Input
              id="tableName"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder="Mon tableau personnalisé"
            />
          </div>

          {/* Configuration des colonnes */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Colonnes</Label>
              <Button onClick={addColumn} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une colonne
              </Button>
            </div>

            <div className="space-y-2">
              {columns.map((column, index) => (
                <div key={column.id} className="flex items-center gap-2 p-2 border rounded">
                  <Input
                    value={column.name}
                    onChange={(e) => updateColumn(column.id, { name: e.target.value })}
                    placeholder="Nom de la colonne"
                    className="flex-1"
                  />
                  <select
                    value={column.type}
                    onChange={(e) => updateColumn(column.id, { type: e.target.value as Column['type'] })}
                    className="bg-background border border-input rounded-md px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  >
                    <option value="text">Texte</option>
                    <option value="number">Nombre</option>
                    <option value="date">Date</option>
                  </select>
                  {columns.length > 1 && (
                    <Button
                      onClick={() => removeColumn(column.id)}
                      size="sm"
                      variant="outline"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Données du tableau */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Données</Label>
              <Button onClick={addRow} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une ligne
              </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr>
                      {columns.map((column) => (
                        <th key={column.id} className="px-4 py-2 text-left font-medium">
                          {column.name}
                        </th>
                      ))}
                      <th className="px-4 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-t">
                        {columns.map((column) => (
                          <td key={column.id} className="px-4 py-2">
                            {editingCell?.rowIndex === rowIndex && editingCell?.columnId === column.id ? (
                              <Input
                                value={row[column.id] || ''}
                                onChange={(e) => handleCellEdit(rowIndex, column.id, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, rowIndex, column.id)}
                                autoFocus
                                className="w-full"
                              />
                            ) : (
                              <div
                                className="min-h-[2rem] px-2 py-1 cursor-pointer hover:bg-muted/50 rounded"
                                onClick={() => setEditingCell({ rowIndex, columnId: column.id })}
                              >
                                {row[column.id] || <span className="text-muted-foreground">Cliquer pour éditer</span>}
                              </div>
                            )}
                          </td>
                        ))}
                        <td className="px-4 py-2">
                          <Button
                            onClick={() => removeRow(rowIndex)}
                            size="sm"
                            variant="ghost"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={onClose} variant="outline">
            Annuler
          </Button>
          <Button onClick={handleSave}>
            {initialData ? 'Modifier' : 'Créer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};